import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { create } from "../../api/API";
import { useCohorts } from "../../hooks/useCohorts";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import FloatingLabelInput from "../Generic/FloatingLabelInput";
import { Row, Col } from "react-bootstrap";
import "react-phone-number-input/style.css";
import "../../styles/Signup.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { GoogleLogin } from "@react-oauth/google";
import { loginUser } from "../../utility/UserUtils";
import CompleteGoogleProfileForm from "./CompleteProfile";
import FloatingLabelPhoneInput from "../Generic/FloatingLabelPhoneInput";

const SignupForm = ({ onSignup }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    cohort: "",
    address: {
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const { cohorts, loading: cohortsLoading } = useCohorts();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Google signup states
  const [googleUser, setGoogleUser] = useState(null);
  const [showGoogleProfileForm, setShowGoogleProfileForm] = useState(false);

  const handleChange = (field, value, nestedField = null) => {
    if ((field === "firstName" || field === "lastName") && !nestedField) {
      // Allow only letters and spaces
      value = value.replace(/[^a-zA-Z\s]/g, "");
    }

    if (nestedField) {
      setForm((prev) => ({
        ...prev,
        [field]: { ...prev[field], [nestedField]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const validatePhone = (phone) => {
    const parsed = parsePhoneNumberFromString(phone);
    if (!parsed || !parsed.isValid())
      return "Please enter a valid phone number with country code";
    return null;
  };

  const validate = () => {
    if (
      !form.firstName ||
      !form.lastName ||
      !form.email ||
      !form.phone ||
      !form.password ||
      !form.confirmPassword ||
      !form.cohort ||
      // !form.address.street ||
      !form.address.city ||
      // !form.address.state ||
      !form.address.zip
      // !form.address.country
    )
      return "Fill all the required fields";
    const phoneError = validatePhone(form.phone);
    if (phoneError) return phoneError;
    if (form.password.length < 6)
      return "Password must be at least 6 characters";
    if (form.password !== form.confirmPassword) return "Passwords do not match";
    return null;
  };

  const hasError = (field) => {
    if (!submitted) return false;

    switch (field) {
      case "firstName":
        return !form.firstName;
      case "lastName":
        return !form.lastName;
      case "email":
        return !form.email;
      case "phone":
        return !form.phone || validatePhone(form.phone);
      case "password":
        return !form.password;
      case "confirmPassword":
        return !form.confirmPassword || form.password !== form.confirmPassword;
      case "city":
        return !form.address.city;
      case "zip":
        return !form.address.zip;
      case "cohort":
        return !form.cohort;
      default:
        return false;
    }
  };

  // -------------------- Email Signup --------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    const errMsg = validate();
    if (errMsg) return toast.error(errMsg);

    try {
      setLoading(true);
      const res = await create("/members", {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        password: form.password,
        cohort: form.cohort,
        address: form.address,
      });

      toast.success(
        "Signup successful! Please check your email for verification link.",
      );

      if (onSignup) onSignup(res);
      navigate("/login");
    } catch (err) {
      const responseData = err.response?.data;
      let message = "Signup failed";

      if (responseData?.errors?.length > 0) {
        const firstError = responseData.errors[0];
        if (firstError?.data?.errors?.length > 0) {
          message = firstError.data.errors[0].message;
        } else if (firstError?.message) {
          message = firstError.message;
        }
      } else if (responseData?.message) {
        message = responseData.message;
      }

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  //Google Signup
  const handleGoogleLoginSuccess = async (credentialResponse) => {
    const token = credentialResponse?.credential;
    if (!token) {
      toast.error("Google token missing. Try again.");
      return;
    }

    try {
      //  Try logging in (user already exists)
      const res = await create("/auth/google-login", { token });

      //  User exists -- Login immediately
      loginUser({ ...res.user, token: res.token });
      window.dispatchEvent(new Event("authChange"));
      toast.success(`Welcome back, ${res.user.firstName || res.user.email}!`);
      navigate("/");
      return; // IMPORTANT
    } catch (err) {
      // STEP User does NOT exist → Proceed to complete profile page
      // console.log("Google user not found → redirecting to complete profile");

      navigate("/auth/google/callback", {
        state: { token }, // pass token to complete-profile page
      });
    }
  };

  const handleGoogleLoginError = () => {
    toast.error("Google login failed. Please try again.");
    // console.log("[GoogleLogin] Login Failed");
  };

  const handleGoogleProfileSubmit = async (extraData) => {
    try {
      setGoogleLoading(true);
      const payload = {
        token: googleUser.token,
        cohort: extraData.cohort,
        address: extraData.address,
      };
      const res = await create("auth/google-signup", payload);
      loginUser({ ...res.user, token: res.token });
      window.dispatchEvent(new Event("authChange"));
      toast.success(`Welcome, ${res.user.firstName || res.user.email}!`);
      navigate("/");
    } catch (err) {
      console.error("Google signup failed:", err);
      toast.error("Google signup failed.");
    } finally {
      setGoogleLoading(false);
      setShowGoogleProfileForm(false);
    }
  };

  return (
    <div
      className="auth-box bg-white"
      style={{ width: "100%", maxWidth: "100%" }}
    >
      {/*EMAIL SIGNUP FORM*/}
      <form onSubmit={handleSubmit}>
        <h2 className="auth-title text-center">Sign up with email</h2>
        <p className="auth-subtext text-black text-center">
          *Required fields are marked with an asterisk
        </p>

        {/* Name */}
        <Row className="mb-3">
          <Col md={6}>
            <FloatingLabelInput
              label="First name*"
              value={form.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
              error={hasError("firstName")}
            />
          </Col>
          <Col md={6}>
            <FloatingLabelInput
              label="Last name*"
              type="text"
              value={form.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
              error={hasError("lastName")}
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <FloatingLabelInput
              label="Email*"
              type="email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              error={hasError("email")}
            />
          </Col>

          <Col md={6}>
            <FloatingLabelPhoneInput
              label="Phone Number"
              required
              value={form.phone}
              onChange={(val) => handleChange("phone", val)}
              error={hasError("phone")}
            />
          </Col>
        </Row>

        {/* Password */}
        <Row className="mb-3">
          <Col md={6} className="position-relative">
            <FloatingLabelInput
              label="Password*"
              type={showPwd ? "text" : "password"}
              value={form.password}
              onChange={(e) => handleChange("password", e.target.value)}
              error={hasError("password")}
            />
            <span
              className="password-icon-signup"
              onClick={() => setShowPwd(!showPwd)}
            >
              {showPwd ? <FaEyeSlash /> : <FaEye />}
            </span>
          </Col>
          <Col md={6} className="position-relative">
            <FloatingLabelInput
              label="Confirm Password*"
              type={showConfirmPwd ? "text" : "password"}
              value={form.confirmPassword}
              onChange={(e) => handleChange("confirmPassword", e.target.value)}
              error={hasError("confirmPassword")}
            />
            <span
              className="password-icon-signup"
              onClick={() => setShowConfirmPwd(!showConfirmPwd)}
            >
              {showConfirmPwd ? <FaEyeSlash /> : <FaEye />}
            </span>
          </Col>
        </Row>

        {/* Address */}
        <Row className="mb-3">
          <Col md={12}>
            <FloatingLabelInput
              label="Street"
              type="text"
              value={form.address.street}
              onChange={(e) =>
                handleChange("address", e.target.value, "street")
              }
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}>
            <FloatingLabelInput
              label="City*"
              type="text"
              value={form.address.city}
              onChange={(e) => handleChange("address", e.target.value, "city")}
              error={hasError("city")}
            />
          </Col>
          <Col md={4}>
            <FloatingLabelInput
              label="State"
              type="text"
              value={form.address.state}
              onChange={(e) => handleChange("address", e.target.value, "state")}
            />
          </Col>
          <Col md={4}>
            <FloatingLabelInput
              label="ZIP Code*"
              type="text"
              value={form.address.zip}
              onChange={(e) => handleChange("address", e.target.value, "zip")}
              error={hasError("zip")}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={12}>
            <FloatingLabelInput
              label="Country"
              type="text"
              value={form.address.country}
              onChange={(e) =>
                handleChange("address", e.target.value, "country")
              }
            />
          </Col>
        </Row>

        {/* Cohort Selection */}
        <div
          className={`mb-3 cohort-section ${hasError("cohort") ? "error" : ""}`}
        >
          <h6>
            SELECT YOUR COHORT*
            <a
              href="/#cohort-structure"
              target="_blank"
              rel="noopener noreferrer"
            >
              (know your cohort)
            </a>
          </h6>
          <div className="cohort-grid">
            {cohorts.map((c) => (
              <div
                key={c.id}
                className={`cohort-box ${
                  form.cohort === c.id ? "selected" : ""
                }`}
                onClick={() => handleChange("cohort", c.id)}
              >
                <div className="cohort-title">{c.title}</div>
                <div className="cohort-range">{c.range}</div>
                <div className="cohort-stage">{c.stage}</div>
              </div>
            ))}
          </div>
        </div>

        <h6>
          By clicking Sign Up, you agree to our&nbsp; 
          <a
            href="/privacypolicy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy.
          </a>
        </h6>

        {/* Submit */}
        <button
          type="submit"
          className="auth-btn bg-primary text-white"
          disabled={loading || googleLoading}
        >
          {loading
            ? "Signing up..."
            : googleLoading
              ? "Signing in with Google..."
              : "Join today"}
        </button>

        {/* OAuth */}
        <div className="auth-divider black">Or</div>
        <div className="auth-socials bg-white text-center">
          <GoogleLogin
            onSuccess={handleGoogleLoginSuccess}
            onError={handleGoogleLoginError}
            useOneTap={false}
            scope="openid profile email"
            prompt="consent"
            context="signup"
            ux_mode="popup"
          />
        </div>

        <p className="auth-footer">Already have an account?</p>
        <p>
          <Link to="/login">Login</Link>
        </p>
      </form>

      {/*Google Profile Completion Modal*/}
      {showGoogleProfileForm && googleUser && (
        <div className="google-profile-overlay">
          <CompleteGoogleProfileForm
            googleUser={googleUser}
            onSubmit={handleGoogleProfileSubmit}
            onCancel={() => setShowGoogleProfileForm(false)}
          />
        </div>
      )}
    </div>
  );
};

export default SignupForm;
